'use client'

import { useEffect, useId, useRef, useState } from 'react'
import { Menu, X, ChevronDown, ArrowRight, ArrowUpRight } from 'lucide-react'
import gsap from 'gsap'
import { useGSAP } from '@gsap/react'

import Button from '/react4xp/components/prototype/Button'
import IconButton from '/react4xp/components/prototype/IconButton'
import LanguageSelector from '/react4xp/components/prototype/LanguageSelector'
import HeaderMenu from '/react4xp/components/prototype/HeaderMenu'

import type { HeaderProps } from './types'

const FOCUSABLE_SELECTOR =
	'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'

export default function Header({
	logo = { src: '/svg/ux/99x-logo.svg', hoverSrc: '/svg/ux/99x-logo-hover.svg', url: '/', alt: '99x' },
	nav = [],
	cta,
	languageOptions = [],
	activeLanguage,
	onLanguageSelect = () => {},
	skipToContentLabel = 'Skip to content',
	skipToContentTarget = '#main-content',
}: HeaderProps) {
	const [openMenuIndex, setOpenMenuIndex] = useState<number | null>(null)
	const [mobileOpen, setMobileOpen] = useState(false)
	const [mobileAccordionIndex, setMobileAccordionIndex] = useState<{ menu: number; column: number } | null>(null)

	const headerRef = useRef<HTMLElement>(null)
	const navRegionRef = useRef<HTMLDivElement>(null)
	const triggerRefs = useRef<(HTMLButtonElement | null)[]>([])
	const hamburgerWrapperRef = useRef<HTMLSpanElement>(null)
	const mobileOverlayRef = useRef<HTMLDivElement>(null)
	const mobilePanelRef = useRef<HTMLDivElement>(null)
	const [mobileMounted, setMobileMounted] = useState(false)
	const [menuMinHeight, setMenuMinHeight] = useState(0)

	const mobileMenuId = useId()

	function closeMenu() {
		setOpenMenuIndex(null)
	}

	// A trigger only opens/closes on an explicit activation — click, Enter, or Space
	// (the browser fires a native click for both keys on a <button>) — never merely on
	// focus, so tabbing through the nav no longer pops menus open.
	function handleTriggerClick(index: number) {
		setOpenMenuIndex((prev) => (prev === index ? null : index))
	}

	// Escape closes the open desktop menu and restores focus to its trigger
	useEffect(() => {
		function handleKeyDown(event: KeyboardEvent) {
			if (event.key !== 'Escape') return
			if (openMenuIndex !== null) {
				const index = openMenuIndex
				closeMenu()
				triggerRefs.current[index]?.focus()
			} else if (mobileOpen) {
				setMobileOpen(false)
				hamburgerWrapperRef.current?.querySelector<HTMLButtonElement>('button')?.focus()
			}
		}
		document.addEventListener('keydown', handleKeyDown)
		return () => document.removeEventListener('keydown', handleKeyDown)
	}, [openMenuIndex, mobileOpen])

	// body scroll lock while any menu (desktop dropdown or mobile overlay) is open
	useEffect(() => {
		if (openMenuIndex !== null || mobileOpen) {
			document.body.style.overflow = 'hidden'
		} else {
			document.body.style.overflow = ''
		}
		return () => {
			document.body.style.overflow = ''
		}
	}, [openMenuIndex, mobileOpen])

	// any click outside the triggers and their panels closes the open desktop menu
	useEffect(() => {
		if (openMenuIndex === null) return

		function handleOutsideClick(event: MouseEvent) {
			if (!navRegionRef.current?.contains(event.target as Node)) closeMenu()
		}

		document.addEventListener('click', handleOutsideClick)
		return () => document.removeEventListener('click', handleOutsideClick)
	}, [openMenuIndex])

	// every desktop submenu takes the height of the tallest one, re-measured when content or width changes
	useEffect(() => {
		const contents = Array.from(navRegionRef.current?.querySelectorAll<HTMLElement>('[data-header-menu-content]') ?? [])
		if (contents.length === 0) return

		const observer = new ResizeObserver(() => setMenuMinHeight(Math.max(...contents.map((el) => el.offsetHeight))))
		contents.forEach((el) => observer.observe(el))
		return () => observer.disconnect()
	}, [nav])

	// header-scoped tab trap while a menu (desktop dropdown or mobile overlay) is open
	useEffect(() => {
		if (openMenuIndex === null && !mobileOpen) return

		function handleTabTrap(event: KeyboardEvent) {
			if (event.key !== 'Tab') return
			const scopeEl = mobileOpen ? mobileOverlayRef.current : headerRef.current
			if (!scopeEl) return

			const focusable = Array.from(scopeEl.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
				(el) => !el.closest('[inert]') && (el.offsetParent !== null || el === document.activeElement),
			)
			if (focusable.length === 0) return

			const first = focusable[0]
			const last = focusable[focusable.length - 1]

			if (event.shiftKey && document.activeElement === first) {
				event.preventDefault()
				last.focus()
			} else if (!event.shiftKey && document.activeElement === last) {
				event.preventDefault()
				first.focus()
			}
		}

		document.addEventListener('keydown', handleTabTrap)
		return () => document.removeEventListener('keydown', handleTabTrap)
	}, [openMenuIndex, mobileOpen])

	// mobile overlay mount/unmount animation (Header owns this — the overlay is Header's own responsibility)
	useEffect(() => {
		if (mobileOpen) setMobileMounted(true)
	}, [mobileOpen])

	useGSAP(
		() => {
			const el = mobilePanelRef.current
			if (!el || !mobileMounted) return
			gsap.killTweensOf(el)

			if (mobileOpen) {
				gsap.fromTo(el, { autoAlpha: 0 }, { autoAlpha: 1, duration: 0.3, ease: 'power2.inOut' })
			} else {
				gsap.to(el, {
					autoAlpha: 0,
					duration: 0.3,
					ease: 'power2.inOut',
					onComplete: () => setMobileMounted(false),
				})
			}
		},
		{ dependencies: [mobileOpen, mobileMounted] },
	)

	function toggleMobileAccordion(menuIndex: number, columnIndex: number) {
		setMobileAccordionIndex((prev) =>
			prev && prev.menu === menuIndex && prev.column === columnIndex ? null : { menu: menuIndex, column: columnIndex },
		)
	}

	return (
		<header ref={headerRef} className="relative bg-black-950 py-6">
			<a
				href={skipToContentTarget}
				className="sr-only focus:not-sr-only focus:absolute focus:top-6 focus:left-6 focus:z-100 focus:rounded-sm focus:bg-brand-500 focus:px-5 focus:py-3 focus:text-base focus:leading-normal focus:font-medium focus:text-black-950"
			>
				{skipToContentLabel}
			</a>

			{/* Swallows the outside click so it closes the menu without also activating page content beneath. */}
			{openMenuIndex !== null && <div aria-hidden="true" className="fixed inset-x-0 top-23 bottom-0 z-30 hidden lg:block" />}

			<div className="container">
				<div className="flex items-center gap-3">
					{/* Three-column row: logo (fill) | nav (hug, centered) | utility (fill, right).
					   Equal-basis flex-1 side columns keep the Navbar horizontally centered in the
					   header — matching Figma's justify-center root with flex-[1_0_0] side containers
					   (node 200:567) — rather than justify-between, which pushes the nav off-centre
					   whenever the logo and utility groups differ in width. Below lg the nav column is
					   hidden, so the two flex-1 columns naturally sit at the row edges. */}
					<div className="flex min-w-0 flex-1 shrink items-center">
						<a href={logo.url} className="group inline-flex">
							<figure className="relative w-18.5 max-w-full">
								<img src={logo.src} alt={logo.alt} className="h-auto w-auto max-w-full transition-opacity duration-200 group-hover:opacity-0 group-focus-visible:opacity-0" />
								{logo.hoverSrc && (
									<img
										src={logo.hoverSrc}
										alt=""
										aria-hidden="true"
										className="absolute inset-0 h-full w-full opacity-0 transition-opacity duration-200 group-hover:opacity-100 group-focus-visible:opacity-100"
									/>
								)}
							</figure>
						</a>
					</div>

					<div ref={navRegionRef} className="hidden shrink-0 lg:block">
						<nav aria-label="Main menu">
							<ul className="flex items-start gap-12">
								{nav.map((item, index) => {
									const hasMenu = Boolean(item.columns && item.columns.length > 0)
									const triggerId = `header-nav-trigger-${index}`
									const panelId = `header-nav-panel-${index}`
									const isOpen = openMenuIndex === index

									if (!hasMenu) {
										if (!item.url) return null

										return (
											<li key={index}>
												<a
													href={item.url}
													className="border-b border-transparent pb-1 text-lg leading-normal font-normal whitespace-nowrap text-white transition-colors duration-200 hover:border-brand-500 focus-visible:border-brand-500"
												>
													{item.label}
												</a>
											</li>
										)
									}

									return (
										<li key={index}>
											<button
												ref={(el) => {
													triggerRefs.current[index] = el
												}}
												id={triggerId}
												type="button"
												aria-expanded={isOpen}
												aria-haspopup="true"
												aria-controls={panelId}
												onClick={() => handleTriggerClick(index)}
												className={`flex cursor-pointer items-center gap-1 border-b pb-1 text-lg leading-normal font-normal whitespace-nowrap text-white transition-colors duration-200 hover:border-brand-500 focus-visible:border-brand-500 ${
													isOpen ? 'border-brand-500' : 'border-transparent'
												}`}
											>
												{item.label}
												<ChevronDown
													className={`size-5 shrink-0 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
													aria-hidden="true"
												/>
											</button>

											<HeaderMenu
												id={panelId}
												isOpen={isOpen}
												columns={item.columns}
												labelledBy={triggerId}
												onClose={closeMenu}
												minHeight={menuMinHeight}
											/>
										</li>
									)
								})}
							</ul>
						</nav>
					</div>

					<div className="flex flex-1 shrink-0 items-center justify-end gap-3">
						{cta && (
							/* Wrapper carries the responsive display toggle — a plain span has no competing
							   display utility, so `hidden`/`lg:*` win over Button's own `inline-flex`
							   (both live in @layer utilities, where source order would otherwise let the
							   component class override the Tailwind display utility). */
							<span className="hidden sm:inline-flex">
								<Button variant="primary" size="sm" href={cta.url}>
									{cta.label}
								</Button>
							</span>
						)}

						<LanguageSelector
							options={languageOptions}
							active={activeLanguage}
							onSelect={onLanguageSelect}
							label="Language"
						/>

						{/* IconButton is not forwardRef-wrapped (React 18) — span wrapper carries the
						   focus-restore ref AND the responsive display toggle (see CTA note above). */}
						<span ref={hamburgerWrapperRef} className="inline-flex lg:hidden">
							<IconButton
								icon={mobileOpen ? X : Menu}
								label={mobileOpen ? 'Close menu' : 'Open menu'}
								variant="square"
								aria-expanded={mobileOpen}
								aria-controls={mobileMenuId}
								active={mobileOpen}
								onClick={() => setMobileOpen((prev) => !prev)}
							/>
						</span>
					</div>
				</div>
			</div>

			<div
				ref={mobileOverlayRef}
				id={mobileMenuId}
				role="dialog"
				aria-modal="true"
				aria-label="Mobile menu"
				aria-hidden={!mobileOpen}
				{...(!mobileOpen ? ({ inert: '' } as Record<string, string>) : {})}
				className={`fixed inset-x-0 top-23 bottom-0 z-40 overflow-y-auto bg-black-950 lg:hidden ${
					mobileOpen ? 'visible opacity-100' : 'invisible opacity-0'
				}`}
			>
				<div ref={mobilePanelRef} className="container pt-3 pb-8 px-3">
					{mobileMounted && (
						<div className="flex flex-col gap-6">
							<nav aria-label="Main menu (mobile)">
								<ul className="flex flex-col gap-6 [&>li]:flex">
									{nav.map((item, menuIndex) => {
										const hasMenu = Boolean(item.columns && item.columns.length > 0)

										if (!hasMenu) {
											if (!item.url) return null

											return (
												<li key={menuIndex}>
													<a
														href={item.url}
														className="flex items-start gap-2 text-lg leading-normal font-normal text-white"
													>
														{item.label}
														{item.isExternal ? (
															<ArrowUpRight className="h-[1lh] w-5 shrink-0 text-brand-500" aria-hidden="true" />
														) : (
															<ArrowRight className="h-[1lh] w-5 shrink-0 text-brand-500" aria-hidden="true" />
														)}
													</a>
												</li>
											)
										}

										return (
											<li key={menuIndex} className="flex flex-col">
												<p className="text-lg leading-normal font-normal text-brand-500">{item.label}</p>

												<ul className="flex flex-col">
													{item.columns?.map((column, columnIndex) => {
														if (column.type === 'promo') {
															if (!column.ctaUrl) return null

															return (
																<li key={columnIndex} className="flex">
																	<a
																		href={column.ctaUrl}
																		className="flex items-start gap-2 py-2 text-lg leading-[1.4] font-medium text-white"
																	>
																		{column.ctaLabel}
																		{column.ctaIsExternal ? (
																			<ArrowUpRight className="h-[1lh] w-5 shrink-0 text-brand-500" aria-hidden="true" />
																		) : (
																			<ArrowRight className="h-[1lh] w-5 shrink-0 text-brand-500" aria-hidden="true" />
																		)}
																	</a>
																</li>
															)
														}

														if (column.links.length === 0) {
															return (
																<li key={columnIndex} className="flex">
																	<a
																		href={column.mainLink.url}
																		className="flex items-start gap-2 py-2 text-lg leading-[1.4] font-medium text-white"
																	>
																		{column.mainLink.label}
																		{column.mainLink.isExternal ? (
																			<ArrowUpRight className="h-[1lh] w-5 shrink-0 text-brand-500" aria-hidden="true" />
																		) : (
																			<ArrowRight className="h-[1lh] w-5 shrink-0 text-brand-500" aria-hidden="true" />
																		)}
																	</a>
																</li>
															)
														}

														const accordionOpen =
															mobileAccordionIndex?.menu === menuIndex && mobileAccordionIndex?.column === columnIndex
														const accordionPanelId = `mobile-accordion-${menuIndex}-${columnIndex}`

														return (
															<li key={columnIndex} className="flex flex-col">
																<button
																	type="button"
																	aria-expanded={accordionOpen}
																	aria-controls={accordionPanelId}
																	onClick={() => toggleMobileAccordion(menuIndex, columnIndex)}
																	className="flex w-full border-b border-transparent items-center justify-between gap-2  text-lg leading-[1.4] font-medium text-white"
																>
																	<span className="py-2">
																		{column.mainLink.label}
																	</span>
																	<span className="flex size-11 shrink-0 items-center justify-center border-l border-brand-500">
																		<ChevronDown
																			className={`size-5 shrink-0 transition-transform duration-200 ${accordionOpen ? 'rotate-180' : ''}`}
																			aria-hidden="true"
																		/>
																	</span>
																</button>
																<ul
																	id={accordionPanelId}
																	aria-hidden={!accordionOpen}
																	{...(!accordionOpen ? ({ inert: '' } as Record<string, string>) : {})}
																	className={accordionOpen ? 'flex flex-col py-2 px-4' : 'hidden'}
																>
																	<li className="flex">
																		<a
																			href={column.mainLink.url}
																			className="block w-full py-2.5 text-left text-base leading-normal font-normal text-white"
																		>
																			{column.mainLink.label}
																			{column.mainLink.isExternal ? (
																				<ArrowUpRight className="ml-2 inline-block h-[1lh] w-5 align-text-top text-brand-500" aria-hidden="true" />
																			) : (
																				<ArrowRight className="ml-2 inline-block h-[1lh] w-5 align-text-top text-brand-500" aria-hidden="true" />
																			)}
																		</a>
																	</li>
																	{column.links.map((link, linkIndex) => (
																		<li key={linkIndex} className="flex border-b border-transparent">
																			<a
																				href={link.url}
																				className="block w-full py-2.5 text-left text-base leading-normal font-normal text-white"
																			>
																				{link.label}
																				{link.isExternal ? (
																					<ArrowUpRight className="ml-2 inline-block h-[1lh] w-5 align-text-top text-brand-500" aria-hidden="true" />
																				) : (
																					<ArrowRight className="ml-2 inline-block h-[1lh] w-5 align-text-top text-brand-500" aria-hidden="true" />
																				)}
																			</a>
																		</li>
																	))}
																</ul>
															</li>
														)
													})}
												</ul>
											</li>
										)
									})}
								</ul>
							</nav>

							{cta && (
								/* sm:hidden mirrors the header CTA's `hidden sm:inline-flex` (see note above) —
								   shown only while the header's own CTA is hidden below the sm breakpoint. */
								<span className="block sm:hidden">
									<Button variant="primary" size="sm" href={cta.url} className="w-full">
										{cta.label}
									</Button>
								</span>
							)}
						</div>
					)}
				</div>
			</div>
		</header>
	)
}
